/* eslint-disable react/button-has-type */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import ZoomButtons from './ZoomButton';
import useZoom from './hook/useZoom';
import Loading from '../Loading';

const StyledDocument = styled(Document)`
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.79);
  max-width: 100%;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const PDFContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const ZoomButtonContainer = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  display: flex;
`;

/**
 * PDFファイルを表示するためのコンポーネント
 *
 * 指定されたPDFファイルを表示し、ズームイン・ズームアウトボタンで拡大・縮小が可能
 * TODO: i18n対応
 *
 * @param {Object} props コンポーネントのプロパティ
 * @param {string} props.file 表示するPDFファイルのURLまたはパス
 * @param {number} props.totalPages 表示するPDFのページ数
 * @param {function} props.onLoadSuccess PDFの読み込み成功時のコールバック
 * @param {Object} props.options react-pdfのPageコンポーネントに渡すオプション
 * @param {Object} documentRef - react-pdf Documentコンポーネントの参照
 * @param {Object[]} pageRefs - react-pdf Pageコンポーネントの参照の配列
 *
 * @returns {ReactElement}
 *
 * @see {@link https://github.com/wojtekmaj/react-pdf/wiki/Recipes#display-all-pages react-pdfの公式ドキュメント}
 *
 *  注意:
 * - pdfをスクロールで表示する場合、全ページを表示させるとパフォーマンスに影響が出る可能性がある
 * - ページ数が少ない場合は、あまり気にしなくて良さそう
 */
const PDFViewer = ({
  file,
  hasError,
  totalPages,
  onLoadSuccess,
  options,
  documentRef,
  pageRefs,
}) => {
  const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(1, 0.5, 1.5, 0.1);

  return (
    <PDFContainer id="scrollArea">
      {!hasError && (
        <Fragment key="preview">
          <StyledDocument
            file={file}
            onLoadSuccess={onLoadSuccess}
            inputRef={documentRef}
            loading={<Loading loadingMessages={['loading']} />}
            noData={<Loading loadingMessages={['loading']} />}
            error={<FormattedMessage id="report.detail.pdf.error" />}
          >
            {Array.from({ length: totalPages }).map((_, index) => (
              <Page
                inputRef={ref => {
                  // eslint-disable-next-line no-param-reassign
                  pageRefs.current[index] = ref;
                }}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                options={options}
                scale={zoomLevel}
              />
            ))}
          </StyledDocument>
          <ZoomButtonContainer>
            <ZoomButtons onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          </ZoomButtonContainer>
        </Fragment>
      )}
    </PDFContainer>
  );
};

PDFViewer.propTypes = {
  file: PropTypes.string.isRequired,
  hasError: PropTypes.bool.isRequired,
  totalPages: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  onLoadSuccess: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
  documentRef: PropTypes.object.isRequired,
  pageRefs: PropTypes.object.isRequired,
};

PDFViewer.defaultProps = {
  totalPages: null,
};

export default PDFViewer;
